import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';

import analytics from '../../../../../modules/analytics';
import { useProvider, useUserState, useAsyncEffect } from '../../../../../hooks';

import MarketingToolTip from './MarketingToolTip';
import { getInsights } from '../../Utils/getInsights';

type Timeout = ReturnType<typeof setTimeout>;

export const LAST_INTERACTION_TIME_KEY = 'LastInsightInteractionTime';

export type MarketingToolTipContainerProps = {
  providerId: string | number;
};

export type Tips = {
  message: string;
  tracking: string;
  duration: number;
  delay: number;
};

export const tips = {
  welcome: {
    message: 'Unlocked: personalized marketing advice',
    tracking: 'pro_p1c_marketing_nav_notification_grow_view',
    delay: 10000,
    duration: 6000,
  },
  reminder: {
    message: 'New marketing advice available',
    tracking: 'pro_marketing_nav_notification_reminder_grow_view',
    delay: 10000,
    duration: 6000,
  },
};

const allowedRoutes = new Set(['provider.todayNext']);

/**
 * Controls the Marketing Tool Tip of the pro navbar that appears on footer of mobile pro pages
 */
export const MarketingToolTipContainer: React.FC<MarketingToolTipContainerProps> = ({
  /** Provider ID of logged in user */
  providerId,
  children,
}) => {
  const [marketingToolTipState, setMarketingToolTipState] = useState<any>({ message: '', visible: false });
  const [, setDurationTimeoutID] = useState<Timeout | null>(null);
  const [insights, setAllInsights] = useState<any[] | null>([]);
  const [toolTipViewed, setToolTipViewed, toolTipViewedLoaded] = useUserState<boolean>('HasViewedMarketingToolTip');
  const [
    interactionTime,
    setInteractionTime,
    interactionTimeLoaded,
  ] = useUserState<string>(LAST_INTERACTION_TIME_KEY);
  const currentRoute = useSelector(state => (state as any).route.name);
  const { provider, loading: providerLoading } = useProvider(providerId);

  const hideMarketingToolTip = (): void => {
    setMarketingToolTipState(current => ({ ...current, visible: false }));
    setDurationTimeoutID((timeoutID: Timeout | null): Timeout | null => {
      clearTimeout(timeoutID);
      return null;
    });
  };

  const showMarketingToolTip = (tip: Tips) => {
    const {
      message,
      tracking,
      duration,
      delay,
    } = tip;
    const show = (): void => {
      setMarketingToolTipState({ message, visible: true });
      setInteractionTime(moment().format());
      setToolTipViewed(true);
      setDurationTimeoutID((timeoutID: Timeout | null): Timeout => {
        clearTimeout(timeoutID);
        return setTimeout(hideMarketingToolTip, duration);
      });
      analytics.track(tracking, { providerId });
    };
    return setTimeout(show, delay);
  };

  const isRecentPhaseOneCompletePro = (): boolean => {
    if (!provider) return false;
    const p1cDate = moment.utc(provider.profile_phase_one_complete_date);
    const past = moment.utc().subtract(1, 'days');
    return (allowedRoutes.has(currentRoute) && p1cDate.isSameOrAfter(past) && !toolTipViewed);
  };

  const hasNotInteractedRecently = (): boolean => {
    const isP1C = provider?.profile_phase_one_complete_date !== null;
    const lastInteraction = moment.utc(interactionTime);
    const past = moment.utc().subtract(7, 'days');
    const hasNewInsights = insights.length > 0;
    return (
      allowedRoutes.has(currentRoute)
      && isP1C
      && lastInteraction.isSameOrBefore(past)
      && hasNewInsights
    );
  };

  const initShowMarketingToolTip = (): Timeout | null => {
    if (isRecentPhaseOneCompletePro()) return showMarketingToolTip(tips.welcome);
    if (hasNotInteractedRecently()) return showMarketingToolTip(tips.reminder);
    return null;
  };

  useAsyncEffect(async () => {
    const insightResults = await getInsights(providerId);
    setAllInsights(insightResults);
  }, [providerId]);

  useEffect(() => {
    if (!toolTipViewedLoaded || !interactionTimeLoaded || providerLoading) return () => {};
    const delayTimeoutID = initShowMarketingToolTip();
    return () => {
      clearTimeout(delayTimeoutID);
    };
  }, [toolTipViewedLoaded, interactionTimeLoaded, providerLoading, interactionTime, insights]);

  return (
    <MarketingToolTip
      message={marketingToolTipState.message}
      testID="marketing-hub"
      visible={marketingToolTipState.visible}
      maxWidth={348}
      onMarketingToolTipBackdropClick={hideMarketingToolTip}
    >
      {children}
    </MarketingToolTip>
  );
};

export default MarketingToolTipContainer;
